import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Card } from "@/components/ui/card";
import {
  GraduationCap,
  ExternalLink,
  Info,
  Wallet,
  CheckCircle2,
  MapPin,
} from "lucide-react";
import musikonzeptLogo from "@/assets/musikonzept-logo-cropped.png";

interface Foerderung {
  bundesland: string;
  titel: string;
  text: string;
  linkText: string;
  url: string;
}

const FOERDERUNGEN: Foerderung[] = [
  {
    bundesland: "Nordrhein-Westfalen",
    titel: "Bildungsscheck 2.0",
    text: "Für Personen mit Wohnsitz in NRW und einem zu versteuernden Jahreseinkommen bis 50.000 € (bei gemeinsamer Veranlagung 100.000 €). Gefördert werden können einmal pro Kalenderjahr 50 % der Kursgebühren, höchstens 500 €. Die Weiterbildung muss vor Kursbeginn im ESF-Portal angemeldet werden; über die Förderung entscheidet die Bezirksregierung.",
    linkText: "Zum Bildungsscheck NRW 2.0",
    url: "https://www.weiterbildung.nrw.de/buergerinnen/finanzierung/bildungsscheck-2",
  },
  {
    bundesland: "Rheinland-Pfalz",
    titel: "QualiScheck",
    text: "Förderprogramm für berufliche Weiterbildung von Beschäftigten mit Hauptwohnsitz oder Arbeitsort in Rheinland-Pfalz, unabhängig vom Einkommen. Gefördert werden können 60 % der Kosten, höchstens 1.500 € pro Weiterbildung und Kalenderjahr. Der Antrag muss spätestens einen Monat vor Beginn gestellt werden; auch an den Bildungsanbieter stellt das Programm Anforderungen.",
    linkText: "Zum QualiScheck Rheinland-Pfalz",
    url: "https://esf.rlp.de/berufliche-weiterbildung/qualischeck",
  },
  {
    bundesland: "Hamburg",
    titel: "Weiterbildungsbonus PLUS",
    text: "Zuschuss für berufliche Weiterbildung für Beschäftigte und Selbstständige, die in Hamburg wohnen oder arbeiten (bei Angestellten u. a. nur bei Arbeitgebern mit weniger als 250 Beschäftigten). Der Antrag muss vor Beginn der Weiterbildung bei Zwei P gestellt werden.",
    linkText: "Zum Weiterbildungsbonus PLUS",
    url: "https://www.zwei-p.org/weiterbildungsbonus",
  },
  {
    bundesland: "Schleswig-Holstein",
    titel: "Weiterbildungsbonus Schleswig-Holstein",
    text: "Förderung beruflicher Weiterbildung (ab 16 Zeitstunden) für Beschäftigte mit Arbeitsstelle in Schleswig-Holstein. Das Land übernimmt 60 % der Kosten, höchstens 5.000 €; mindestens 40 % trägt der Arbeitgeber. Der Antrag geht spätestens vier Wochen vor Beginn an die Investitionsbank Schleswig-Holstein (IB.SH).",
    linkText: "Zum Weiterbildungsbonus Schleswig-Holstein",
    url: "https://www.ib-sh.de/produkt/a3-weiterbildungsbonus-schleswig-holstein/",
  },
  {
    bundesland: "Brandenburg",
    titel: "Bildungsscheck Brandenburg",
    text: "Der Bildungsscheck nach der Weiterbildungsrichtlinie 2026 fördert berufliche Weiterbildung von Beschäftigten mit Erstwohnsitz oder Arbeitsvertrag in Brandenburg mit 60 % der förderfähigen Kosten; der Zuschuss muss mindestens 500 € betragen. Anträge nimmt die Investitionsbank des Landes Brandenburg (ILB) entgegen.",
    linkText: "Zum Bildungsscheck Brandenburg",
    url: "https://www.ilb.de/de/weiterbildungsrichtlinie-2026/",
  },
  {
    bundesland: "Sachsen",
    titel: "Berufliche Weiterbildung",
    text: "Sachsen hat die Förderung individueller berufsbezogener Weiterbildung derzeit ausgesetzt; die Sächsische Aufbaubank (SAB) nimmt zurzeit keine Anträge an. Ob und wann das Programm wieder öffnet, steht auf der Seite der SAB.",
    linkText: "Zur SAB-Förderung berufliche Weiterbildung",
    url: "https://www.sab.sachsen.de/berufliche-weiterbildung-individuell-berufsbezogene-weiterbildung",
  },
  {
    bundesland: "Bundesweit – Solo-Selbstständige",
    titel: "KOMPASS",
    text: "Für Solo-Selbstständige im Haupterwerb (mindestens zwei Jahre am Markt) kann das bundesweite ESF-Plus-Programm KOMPASS interessant sein. Nach einer verpflichtenden Beratung bei einer KOMPASS-Anlaufstelle kann ein Qualifizierungsscheck über bis zu 90 % der Kosten, höchstens 4.500 €, ausgestellt werden; ob derzeit Schecks vergeben werden, sagt die Anlaufstelle.",
    linkText: "Zum KOMPASS-Programm",
    url: "https://www.esf.de/portal/DE/ESF-Plus-2021-2027/Foerderprogramme/bmas/kompass.html",
  },
];

const HIGHLIGHTS = [
  { icon: Wallet, label: "Bis zu 500 € Zuschuss in NRW" },
  { icon: CheckCircle2, label: "Fachkraft für Musik: staatlich zugelassen (ZFU)" },
  { icon: MapPin, label: "Programme in 6 Bundesländern" },
];

const Foerdermoeglichkeiten = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/[0.03] via-background to-background">
      <SEO
        title="Fördermöglichkeiten für deine Weiterbildung | MusiKonzept"
        description="Bildungsscheck NRW, QualiScheck, Weiterbildungsbonus & Co. – Überblick über Förderprogramme für unsere musikpädagogischen Weiterbildungen."
        path="/foerdermoeglichkeiten"
      />
      <Header />
      <main className="pb-20">
        {/* Hero */}
        <section className="relative overflow-hidden pt-28 pb-16">
          {/* Decorative blobs */}
          <div className="absolute inset-0 -z-10">
            <div className="absolute top-10 -left-20 w-80 h-80 rounded-full bg-primary/10 blur-3xl" />
            <div className="absolute top-32 right-0 w-96 h-96 rounded-full bg-accent/10 blur-3xl" />
          </div>

          <div className="container mx-auto px-6 max-w-5xl">
            <div className="flex flex-col items-center text-center space-y-6">
              <img
                src={musikonzeptLogo}
                alt="MusiKonzept"
                className="h-12 md:h-14 w-auto"
              />

              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-primary/80">
                <span className="h-px w-8 bg-primary/40" />
                Aktueller Stand: Oktober 2026
                <span className="h-px w-8 bg-primary/40" />
              </div>

              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground max-w-3xl leading-tight">
                Lass dir deine Weiterbildung{" "}
                <span className="relative inline-block">
                  <span className="relative z-10 text-primary">fördern</span>
                  <span className="absolute inset-x-0 bottom-1 h-3 bg-accent/30 -z-0 rounded-sm" />
                </span>
              </h1>

              <p className="text-lg text-muted-foreground max-w-2xl">
                Viele Bundesländer fördern berufliche Weiterbildung. Unsere Ausbildung „Fachkraft für Musik im
                Elementarbereich“ ist ein staatlich zugelassener Fernlehrgang (ZFU). Hier findest du die wichtigsten
                Programme auf einen Blick; ob ein Programm deine Ausbildung fördert, entscheidet die jeweilige Stelle.
              </p>

              {/* Highlight pills */}
              <div className="flex flex-wrap justify-center gap-3 pt-2">
                {HIGHLIGHTS.map((h) => (
                  <div
                    key={h.label}
                    className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-background/80 backdrop-blur px-4 py-2 text-sm font-medium text-foreground shadow-sm"
                  >
                    <h.icon className="w-4 h-4 text-primary" />
                    {h.label}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <div className="container mx-auto px-6 max-w-5xl">
          {/* Intro */}
          <Card className="relative overflow-hidden p-6 md:p-8 mb-12 border-primary/20 shadow-md">
            <div className="absolute top-0 left-0 h-full w-1.5 bg-gradient-to-b from-primary to-accent" />
            <div className="flex flex-col md:flex-row gap-5 md:items-start pl-3">
              <div className="shrink-0">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center shadow-lg shadow-primary/20">
                  <GraduationCap className="w-6 h-6 text-primary-foreground" />
                </div>
              </div>
              <div className="space-y-3 text-muted-foreground leading-relaxed">
                <p>
                  Für berufliche Weiterbildungen gibt es in einigen Bundesländern finanzielle
                  Förderprogramme. Je nach Wohnort, Beschäftigungsstatus und persönlicher Situation
                  kann es möglich sein, einen Zuschuss zu den Weiterbildungskosten zu erhalten.
                </p>
                <p>
                  Besonders bekannt ist der{" "}
                  <strong className="text-foreground">Bildungsscheck NRW 2.0</strong>. Auch andere
                  Bundesländer bieten ähnliche Programme an – teilweise unter anderen Namen wie
                  QualiScheck, Weiterbildungsbonus oder berufliche Weiterbildungsförderung.
                </p>
                <p className="text-sm">
                  Bitte prüfe die Voraussetzungen immer direkt bei der zuständigen Förderstelle,
                  bevor du dich verbindlich anmeldest.
                </p>
              </div>
            </div>
          </Card>

          {/* Förderprogramme Grid */}
          <h2 className="text-2xl md:text-3xl font-bold text-foreground text-center mb-2">
            Förderprogramme nach Bundesland
          </h2>
          <p className="text-muted-foreground text-center mb-8">
            Klicke dich direkt zur jeweiligen Förderstelle.
          </p>

          <div className="grid gap-5 md:grid-cols-2 mb-12">
            {FOERDERUNGEN.map((f) => (
              <Card
                key={f.titel}
                className="group relative overflow-hidden p-6 flex flex-col h-full border-border/60 hover:border-primary/40 hover:shadow-xl hover:shadow-primary/5 transition-all duration-300"
              >
                {/* Top accent bar */}
                <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-primary via-primary/60 to-accent opacity-60 group-hover:opacity-100 transition-opacity" />

                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="w-3.5 h-3.5 text-accent" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                    {f.bundesland}
                  </span>
                </div>

                <h3 className="text-xl font-bold text-foreground mb-3">{f.titel}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-5 flex-1">
                  {f.text}
                </p>

                <a
                  href={f.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-between gap-2 text-sm font-semibold text-primary hover:text-primary/80 transition-colors group/link"
                >
                  <span>{f.linkText}</span>
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary/10 group-hover/link:bg-primary group-hover/link:text-primary-foreground transition-all">
                    <ExternalLink className="w-3.5 h-3.5" />
                  </span>
                </a>
              </Card>
            ))}
          </div>

          {/* Hinweis */}
          <Card className="relative overflow-hidden p-6 md:p-7 bg-gradient-to-br from-accent/10 via-accent/5 to-transparent border-accent/30">
            <div className="flex gap-4">
              <div className="shrink-0">
                <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center">
                  <Info className="w-5 h-5 text-accent" />
                </div>
              </div>
              <div className="space-y-2">
                <p className="font-semibold text-foreground">Wichtiger Hinweis · Stand Oktober 2026</p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Förderprogramme, Bedingungen und Höchstbeträge können sich kurzfristig ändern,
                  ausgesetzt werden oder regional unterschiedlich ausfallen. Bitte prüfe die
                  aktuellen Voraussetzungen, Fristen und Förderhöhen immer direkt auf der jeweiligen
                  Seite der zuständigen Förderstelle. Wir können keine Aktualität oder verbindliche
                  Förderzusage garantieren – eine Förderung muss in der Regel vor Beginn der
                  Weiterbildung beantragt werden. Gerne stellen wir dir eine Kursbeschreibung oder
                  ein Angebot für deine Anfrage zur Verfügung.
                </p>
              </div>
            </div>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Foerdermoeglichkeiten;
